import { mkdir, readFile, rm, writeFile } from "node:fs/promises";
import { join } from "node:path";
import process from "node:process";
import {
  normalizeWakeLoopProfileId,
  locksDir as resolveLocksDir,
} from "./paths";
import { resolveCliContextId } from "./session-profile";

export type WakeLoopLock = {
  path: string;
};

export type WakeLoopLockMeta = Record<string, unknown>;
type HeldLockReleaseContext = Record<string, unknown>;

export class WakeLoopLockError extends Error {
  readonly code = "wakeloop.locked";
  readonly lockPath: string;
  readonly lockKey: string;
  readonly metaRaw: string | null;
  readonly meta: WakeLoopLockMeta | null;

  constructor(input: { path: string; key: string; metaRaw?: string }) {
    const normalizedMetaRaw = normalizeMetaRaw(input.metaRaw);
    super(buildWakeLoopLockErrorMessage(input.path, normalizedMetaRaw));
    this.name = "WakeLoopLockError";
    this.lockPath = input.path;
    this.lockKey = input.key;
    this.metaRaw = normalizedMetaRaw;
    this.meta = parseWakeLoopLockMeta(normalizedMetaRaw);
  }
}

export function isWakeLoopLockError(
  error: unknown
): error is WakeLoopLockError {
  return error instanceof WakeLoopLockError;
}

export async function acquireLock(input: {
  wakeLoopProfileId: string;
  key: string;
  meta?: WakeLoopLockMeta;
}): Promise<WakeLoopLock> {
  const wakeLoopProfileId = normalizeWakeLoopProfileId(input.wakeLoopProfileId);
  const sessionId = resolveCliContextId();
  const dir = resolveLocksDir(wakeLoopProfileId);
  await mkdir(dir, { recursive: true });

  const path = resolveLockPath({
    wakeLoopProfileId,
    key: input.key,
  });

  // Exclusive create, with basic stale-lock recovery.
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      await writeFile(
        path,
        `${JSON.stringify({
          pid: process.pid,
          wakeLoopProfileId,
          ...(sessionId ? { sessionId } : {}),
          at: new Date().toISOString(),
          ...(input.meta ? { ...input.meta } : {}),
        })}\n`,
        {
          encoding: "utf8",
          flag: "wx",
        }
      );
      return { path };
    } catch {
      const existingLock = await readLockSnapshot(path);

      if (await tryCleanupStaleLockPath(path, existingLock)) {
        continue;
      }

      throw new WakeLoopLockError({
        path,
        key: input.key,
        metaRaw: existingLock.metaRaw ?? undefined,
      });
    }
  }

  throw new WakeLoopLockError({
    path,
    key: input.key,
  });
}

export async function releaseLock(lock: WakeLoopLock): Promise<void> {
  await rm(lock.path, { force: true });
}

export async function runWithCleanupPreservingRunError<T>(input: {
  run: () => Promise<T>;
  cleanup: () => Promise<void>;
  onCleanupErrorAfterRunFailure?: (cleanupError: unknown) => void;
}): Promise<T> {
  let runResult: T | undefined;
  let runSucceeded = false;
  let runError: unknown;

  try {
    runResult = await input.run();
    runSucceeded = true;
  } catch (error) {
    runError = error;
  }

  try {
    await input.cleanup();
  } catch (cleanupError) {
    if (!runSucceeded) {
      input.onCleanupErrorAfterRunFailure?.(cleanupError);
      throw runError;
    }
    throw cleanupError;
  }

  if (!runSucceeded) {
    throw runError;
  }

  return runResult as T;
}

export async function runWithHeldLock<T>(input: {
  lock: WakeLoopLock;
  run: () => Promise<T>;
  releaseContext?: HeldLockReleaseContext;
}): Promise<T> {
  return await runWithCleanupPreservingRunError({
    run: input.run,
    cleanup: () => releaseLock(input.lock),
    onCleanupErrorAfterRunFailure: (releaseError) => {
      warnLockReleaseFailure({
        error: releaseError,
        lock: input.lock,
        releaseContext: input.releaseContext,
      });
    },
  });
}

export async function readLockMeta(input: {
  wakeLoopProfileId: string;
  key: string;
}): Promise<WakeLoopLockMeta | null> {
  const existingLock = await readLockSnapshot(
    resolveLockPath({
      wakeLoopProfileId: input.wakeLoopProfileId,
      key: input.key,
    })
  );
  return existingLock.meta;
}

export async function tryCleanupStaleLock(input: {
  wakeLoopProfileId: string;
  key: string;
}): Promise<boolean> {
  return await tryCleanupStaleLockPath(
    resolveLockPath({
      wakeLoopProfileId: input.wakeLoopProfileId,
      key: input.key,
    })
  );
}

type LockSnapshot = {
  metaRaw: string | null;
  meta: WakeLoopLockMeta | null;
  pid: number | null;
  hasStalePid: boolean;
};

function resolveLockPath(input: {
  wakeLoopProfileId: string;
  key: string;
}): string {
  const wakeLoopProfileId = normalizeWakeLoopProfileId(input.wakeLoopProfileId);
  return join(
    resolveLocksDir(wakeLoopProfileId),
    `${sanitizeFilename(input.key)}.lock`
  );
}

function sanitizeFilename(value: string): string {
  return value.replace(/[^a-zA-Z0-9._-]+/g, "_");
}

function safeParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function normalizeMetaRaw(metaRaw: string | undefined): string | null {
  if (typeof metaRaw !== "string") {
    return null;
  }
  const normalized = metaRaw.trim();
  return normalized.length > 0 ? normalized : null;
}

function parseWakeLoopLockMeta(
  metaRaw: string | null
): WakeLoopLockMeta | null {
  if (!metaRaw) {
    return null;
  }
  const parsed = safeParse(metaRaw);
  if (!parsed || typeof parsed !== "object") {
    return null;
  }
  return parsed as WakeLoopLockMeta;
}

function buildWakeLoopLockErrorMessage(
  path: string,
  metaRaw: string | null
): string {
  if (!metaRaw) {
    return `space is locked (${path})`;
  }
  return `space is locked (${path}). Meta: ${metaRaw}`;
}

function getPidFromMeta(meta: unknown): number | null {
  if (!meta || typeof meta !== "object") {
    return null;
  }
  const pid = (meta as Record<string, unknown>).pid;
  return typeof pid === "number" ? pid : null;
}

async function readLockSnapshot(path: string): Promise<LockSnapshot> {
  const metaRaw = await readFile(path, "utf8").catch((error) => {
    if (getErrorCode(error) === "ENOENT") {
      return null;
    }
    throw error;
  });
  const normalizedMetaRaw = normalizeMetaRaw(metaRaw ?? undefined);
  if (normalizedMetaRaw === null) {
    return {
      metaRaw: null,
      meta: null,
      pid: null,
      hasStalePid: false,
    };
  }

  const meta = parseWakeLoopLockMeta(normalizedMetaRaw);
  const pid = getPidFromMeta(meta);
  return {
    metaRaw: normalizedMetaRaw,
    meta,
    pid,
    hasStalePid: pid !== null && !isPidAlive(pid),
  };
}

async function tryCleanupStaleLockPath(
  path: string,
  initialSnapshot?: LockSnapshot
): Promise<boolean> {
  const firstSnapshot = initialSnapshot ?? (await readLockSnapshot(path));

  if (
    firstSnapshot.metaRaw === null ||
    firstSnapshot.pid === null ||
    !firstSnapshot.hasStalePid
  ) {
    return false;
  }

  const confirmedSnapshot = await readLockSnapshot(path);
  if (
    confirmedSnapshot.metaRaw !== firstSnapshot.metaRaw ||
    confirmedSnapshot.pid !== firstSnapshot.pid ||
    !confirmedSnapshot.hasStalePid
  ) {
    return false;
  }

  await rm(path, { force: true });
  return true;
}

function isPidAlive(pid: number): boolean {
  try {
    process.kill(pid, 0);
    return true;
  } catch (err) {
    if (getErrorCode(err) === "ESRCH") {
      return false;
    }
    return true;
  }
}

function getErrorCode(err: unknown): string | undefined {
  if (!err || typeof err !== "object") {
    return undefined;
  }
  const code = (err as { code?: unknown }).code;
  return typeof code === "string" ? code : undefined;
}

function warnLockReleaseFailure(input: {
  error: unknown;
  lock: WakeLoopLock;
  releaseContext?: HeldLockReleaseContext;
}): void {
  console.warn("[wakeloop.lock.release.warning]", {
    lockPath: input.lock.path,
    releaseError: toErrorMessage(input.error),
    releaseContext: input.releaseContext ?? {},
  });
}

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
